import CrmAPI from '../../api/crm';

const state = {
  pipelines: [],
  currentPipeline: null,
  cards: {},
  uiFlags: {
    isFetchingPipelines: false,
    isCreatingPipeline: false,
    isFetchingCards: false,
    isCreatingCard: false,
    isMovingCard: false,
  },
};

const getters = {
  getPipelines: _state => _state.pipelines,
  getCurrentPipeline: _state => _state.currentPipeline,
  getCardsByStage: _state => stageId => {
    return (_state.cards[stageId] || []).sort((a, b) => a.position - b.position);
  },
  getAllCards: _state => {
    return Object.values(_state.cards).flat();
  },
  getUIFlags: _state => _state.uiFlags,
};

const mutations = {
  SET_PIPELINES(_state, pipelines) {
    _state.pipelines = pipelines;
  },
  SET_CURRENT_PIPELINE(_state, pipeline) {
    _state.currentPipeline = pipeline;
  },
  ADD_PIPELINE(_state, pipeline) {
    _state.pipelines.push(pipeline);
  },
  UPDATE_PIPELINE(_state, pipeline) {
    const index = _state.pipelines.findIndex(p => p.id === pipeline.id);
    if (index !== -1) {
      _state.pipelines.splice(index, 1, pipeline);
    }
    if (_state.currentPipeline?.id === pipeline.id) {
      _state.currentPipeline = pipeline;
    }
  },
  REMOVE_PIPELINE(_state, pipelineId) {
    _state.pipelines = _state.pipelines.filter(p => p.id !== pipelineId);
    if (_state.currentPipeline?.id === pipelineId) {
      _state.currentPipeline = null;
    }
  },
  SET_CARDS(_state, { stageId, cards }) {
    _state.cards = { ..._state.cards, [stageId]: cards };
  },
  ADD_CARD(_state, card) {
    const stageCards = _state.cards[card.stage_id] || [];
    _state.cards = { ..._state.cards, [card.stage_id]: [...stageCards, card] };
  },
  UPDATE_CARD(_state, card) {
    const stageCards = _state.cards[card.stage_id] || [];
    const index = stageCards.findIndex(c => c.id === card.id);
    if (index !== -1) {
      const updated = [...stageCards];
      updated.splice(index, 1, card);
      _state.cards = { ..._state.cards, [card.stage_id]: updated };
    }
  },
  MOVE_CARD(_state, { card, fromStageId, toStageId, position }) {
    // Remove from old stage
    const fromCards = (_state.cards[fromStageId] || []).filter(c => c.id !== card.id);
    // Add to new stage at position
    const toCards = [...(_state.cards[toStageId] || [])];
    const updatedCard = { ...card, stage_id: toStageId, position };
    toCards.splice(position, 0, updatedCard);
    _state.cards = {
      ..._state.cards,
      [fromStageId]: fromCards,
      [toStageId]: toCards.map((c, i) => ({ ...c, position: i })),
    };
  },
  REMOVE_CARD(_state, { cardId, stageId }) {
    const stageCards = (_state.cards[stageId] || []).filter(c => c.id !== cardId);
    _state.cards = { ..._state.cards, [stageId]: stageCards };
  },
  SET_UI_FLAG(_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
};

const actions = {
  fetchPipelines: async ({ commit }) => {
    commit('SET_UI_FLAG', { isFetchingPipelines: true });
    try {
      const { data } = await CrmAPI.get();
      commit('SET_PIPELINES', data);
      if (data.length > 0 && !state.currentPipeline) {
        commit('SET_CURRENT_PIPELINE', data[0]);
      }
    } catch (error) {
      // handle error
    } finally {
      commit('SET_UI_FLAG', { isFetchingPipelines: false });
    }
  },

  createPipeline: async ({ commit }, pipelineData) => {
    commit('SET_UI_FLAG', { isCreatingPipeline: true });
    try {
      const { data } = await CrmAPI.create({ pipeline: pipelineData });
      commit('ADD_PIPELINE', data);
      commit('SET_CURRENT_PIPELINE', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingPipeline: false });
    }
  },

  updatePipeline: async ({ commit }, { id, ...pipelineData }) => {
    const { data } = await CrmAPI.update(id, { pipeline: pipelineData });
    commit('UPDATE_PIPELINE', data);
    return data;
  },

  deletePipeline: async ({ commit }, pipelineId) => {
    await CrmAPI.delete(pipelineId);
    commit('REMOVE_PIPELINE', pipelineId);
  },

  setCurrentPipeline: ({ commit }, pipeline) => {
    commit('SET_CURRENT_PIPELINE', pipeline);
  },

  fetchCards: async ({ commit }, { pipelineId, stageId }) => {
    commit('SET_UI_FLAG', { isFetchingCards: true });
    try {
      const params = stageId ? { stage_id: stageId } : {};
      const { data } = await CrmAPI.getCards(pipelineId, params);
      // Group by stage
      const grouped = {};
      data.forEach(card => {
        if (!grouped[card.stage_id]) grouped[card.stage_id] = [];
        grouped[card.stage_id].push(card);
      });
      Object.entries(grouped).forEach(([sid, cards]) => {
        commit('SET_CARDS', { stageId: parseInt(sid), cards });
      });
    } finally {
      commit('SET_UI_FLAG', { isFetchingCards: false });
    }
  },

  createCard: async ({ commit }, { pipelineId, cardData }) => {
    commit('SET_UI_FLAG', { isCreatingCard: true });
    try {
      const { data } = await CrmAPI.createCard(pipelineId, { card: cardData });
      commit('ADD_CARD', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingCard: false });
    }
  },

  updateCard: async ({ commit }, { pipelineId, cardId, cardData }) => {
    const { data } = await CrmAPI.updateCard(pipelineId, cardId, { card: cardData });
    commit('UPDATE_CARD', data);
    return data;
  },

  deleteCard: async ({ commit }, { pipelineId, cardId, stageId }) => {
    await CrmAPI.deleteCard(pipelineId, cardId);
    commit('REMOVE_CARD', { cardId, stageId });
  },

  moveCard: async ({ commit }, { pipelineId, cardId, fromStageId, toStageId, position, card }) => {
    commit('SET_UI_FLAG', { isMovingCard: true });
    try {
      commit('MOVE_CARD', { card, fromStageId, toStageId, position });
      await CrmAPI.moveCard(pipelineId, cardId, toStageId, position);
    } catch (error) {
      // Revert on error
      commit('MOVE_CARD', { card: { ...card, stage_id: toStageId }, fromStageId: toStageId, toStageId: fromStageId, position: card.position });
    } finally {
      commit('SET_UI_FLAG', { isMovingCard: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
